import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, message, Modal, Input, Form, Switch } from 'antd';
import Sidebar from './Sidebar';
import TriggerNode from './nodes/TriggerNode';
import AIModelNode from './nodes/AIModelNode';
import OutputNode from './nodes/OutputNode';
import SendEmailNode from './nodes/SendEmailNode';
import HumanInputNode from './nodes/HumanInputNode';
import ConditionNode from './nodes/ConditionNode';  
import PostNode from './nodes/PostNode';
import TemplateModal from './TemplateModal';
import ParserNode from './nodes/ParserNode';
import ChatNode from './nodes/ChatNode';
import ResearchNode from './nodes/ResearchNode';
import FirecrawlNode from './nodes/FirecrawlNode';
import UnsplashNode from './nodes/UnsplashNode';
import CreateFileNode from './nodes/CreateFileNode';
import APICallNode from './nodes/APICallNode';
import MultimediaGeneratorNode from './nodes/MultimediaGeneratorNode';
import { defaultTemplates } from './defaultTemplates';
import WorkflowActionsModal from './WorkflowActionsModal';
import WorkflowNameHeader from './WorkflowNameHeader';
import ExecutionStatusBar from './ExecutionStatusBar';
import WorkflowDetailsModal from './WorkflowDetailsModal';
// import WorkflowAssistant from './WorkflowAssistant';
import { AnnotationToolbar, StickyNote, TextAnnotation, Shape } from './AnnotationTools';
import CustomEdge from './CustomEdge';
import axios from 'axios';
import { adminInstance } from './api/axiosConfig';
import config from '../config';
import { SentimentAnalysisNode, SummaryGeneratorNode, ExtractInformationNode, WriteArticleNode, OptimizeSEONode } from './nodes/SpecializedAINodes';
import {BulbOutlined, SunOutlined, MoonOutlined } from '@ant-design/icons';
import EmbedCodeModal from './EmbedCodeModal';

const nodeTypes = {
  trigger: TriggerNode,
  aiModel: AIModelNode,
  output: OutputNode,
  humanInput: HumanInputNode,
  sendEmail: SendEmailNode,
  condition: ConditionNode,
  post: PostNode,
  sentimentAnalysis: SentimentAnalysisNode,
  summaryGenerator: SummaryGeneratorNode,
  extractInformation: ExtractInformationNode,
  writeArticle: WriteArticleNode,
  optimizeSEO: OptimizeSEONode,
  research: ResearchNode,
  parser: ParserNode,
  firecrawl: FirecrawlNode,
  unsplash: UnsplashNode,
  chat: ChatNode,
  createFile: CreateFileNode,
  APICall: APICallNode,
  mediaGenerator: MultimediaGeneratorNode,
  stickyNote: StickyNote,
  textAnnotation: TextAnnotation,
  shape: Shape
  
};

const edgeTypes = {
  default: CustomEdge,
};


const WorkflowBuilderInner = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const { setViewport } = useReactFlow();
  const [isExecuting, setIsExecuting] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [isSavedWorkflow, setIsSavedWorkflow] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isTemplateSelectionModalVisible, setIsTemplateSelectionModalVisible] = useState(false);
  const [isSaveTemplateModalVisible, setIsSaveTemplateModalVisible] = useState(false);
  const [isActionsModalVisible, setIsActionsModalVisible] = useState(false);
  const [currentExecutingNode, setCurrentExecutingNode] = useState(null);
  const [completionTime, setCompletionTime] = useState(null);
  const [executionError, setExecutionError] = useState(false);
  const [executionProgress, setExecutionProgress] = useState([]);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [colorMode, setColorMode] = useState('light');
  const workflowAssistantRef = useRef(null);
  const [embedModalVisible, setEmbedModalVisible] = useState(false);

  

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          // Handle settings separately from other data
          const updatedSettings = newData.settings 
            ? { ...node.data.settings, ...newData.settings }
            : node.data.settings;
  
          return {
            ...node,
            data: { 
              ...node.data, 
              ...newData,
              settings: updatedSettings,
              workflowId, // Keep workflowId
              updateNodeData, // Keep function reference
              onDelete: node.data.onDelete, // Keep delete function
            },
          };
        }
        return node;
      })
    );
  }, [setNodes, workflowId]);

  const toggleDarkMode = useCallback(() => {
    setColorMode((prev) => prev === 'light' ? 'dark' : 'light');
  }, []);

  const getSelectedNode = useCallback(() => {
    return nodes.find(node => node.selected);
  }, [nodes]);



  useEffect(() => {
    const savedMode = localStorage.getItem('wp-ai-workflows-color-mode');
    if (savedMode) {
      setColorMode(savedMode);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('wp-ai-workflows-color-mode', colorMode);
  }, [colorMode]);
  
  useEffect(() => {
    console.log("Component mounted. Checking for existing workflow data...");
    const settings = window.wpAiWorkflowsSettings || {};
    const { page, workflowData } = settings;
  
    if (page === 'edit' && workflowData) {
      console.log("Loading existing workflow...");
      if (Array.isArray(workflowData.nodes)) {
        setNodes(workflowData.nodes.map(node => {
          // Restore handlers for annotation nodes
          if (['stickyNote', 'textAnnotation', 'shape'].includes(node.type)) {
            return {
              ...node,
              data: {
                ...node.data,
                onChange: (id, updates) => {
                  setNodes(nds => 
                    nds.map(n => 
                      n.id === id 
                        ? { 
                            ...n, 
                            data: { 
                              ...n.data, 
                              ...updates,
                              onChange: n.data.onChange,
                              onDelete: n.data.onDelete 
                            } 
                          }
                        : n
                    )
                  );
                },
                onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
              }
            };
          }
          // Handle other node types
          return {
            ...node,
            data: {
              ...node.data,
              updateNodeData,
              onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id)),
              workflowId: workflowData.id
            }
          };
        }));
        setEdges(workflowData.edges || []);
        if (workflowData.name) {
          setWorkflowName(workflowData.name);
        }
        setWorkflowId(workflowData.id || null);
        setIsEditMode(true);
        setIsSavedWorkflow(true);
        if (workflowData.viewport) {
          setViewport(workflowData.viewport);
        }
      }
    }else {
      setIsTemplateSelectionModalVisible(true);
    }
  }, []);

  useEffect(() => {
    if (reactFlowInstance && workflowAssistantRef.current) {
      workflowAssistantRef.current.updateWorkflowContext({
        nodes,
        edges,
        viewport: reactFlowInstance.getViewport(),
        name: workflowName,
        id: workflowId,
        isExecuting,
        isSaved: isSavedWorkflow
      });
    }
  }, [nodes, edges, workflowName, workflowId, isExecuting, isSavedWorkflow, reactFlowInstance]);

  const loadDefaultTemplate = async (templateId) => {
    const template = defaultTemplates[templateId];
    if (!template) {
      throw new Error('Template not found');
    }
    return template;
  };

  const handleEdgeDelete = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  }, [setEdges]);

  const handleImport = useCallback((importedData) => {
    try {
      // Convert imported data to nodes with required functions
      const importedNodes = importedData.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          updateNodeData,
          onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
        }
      }));
  
      setNodes(importedNodes);
      setEdges(importedData.edges);
      if (importedData.name) {
        setWorkflowName(importedData.name);
      }
      message.success('Workflow imported successfully');
    } catch (error) {
      message.error('Error importing workflow: ' + error.message);
    }
  }, [setNodes, setEdges, setWorkflowName, updateNodeData]);

  const handleExport = () => {
    // Clean node data by removing execution-related fields
    const cleanedNodes = nodes.map(node => {
      const cleanedNode = { ...node };
      const newData = {};
      
      // Copy only non-execution related fields
      Object.keys(node.data || {}).forEach(key => {
        if (!['output', 'displayOutput', 'isExecuting', 'executed', 'executionError', 
             'executionTime', 'lastExecutionTime', 'crawlResults', 'selectedPages', 
             'executionProgress', 'executionStatus'].includes(key)) {
          newData[key] = node.data[key];
        }
      });
      
      cleanedNode.data = newData;
      return cleanedNode;
    });
  
    const workflowData = {
      name: workflowName || 'Untitled Workflow',
      nodes: cleanedNodes,
      edges: edges,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
  
    const blob = new Blob([JSON.stringify(workflowData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${workflowName || 'workflow'}-${new Date().getTime()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleActionModalOpen = () => {
    setIsActionsModalVisible(true);
  };

  const viewExecutionDetails = async (executionId) => {
    try {
      const response = await adminInstance.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/executions/${executionId}`
      );
      let execution = response.data;
      
      // Process output_data if it's a string
      if (typeof execution.output_data === 'string') {
        try {
          execution.output_data = JSON.parse(execution.output_data);
        } catch (e) {
          console.error('Failed to parse output_data:', e);
          execution.output_data = {};
        }
      }
      
      setSelectedExecution(execution);
      setIsDetailsModalVisible(true);
    } catch (error) {
      console.error('Error fetching execution details:', error);
      message.error('Failed to fetch execution details');
    }
  };

  const handleSelectTemplate = async (templateId, isDefault = false) => {
    try {
      let templateData;
      if (isDefault) {
        templateData = await loadDefaultTemplate(templateId);
      } else {
        // Extract the actual ID by removing the "user-" prefix
        const actualId = templateId.replace('user-', '');
        const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/templates/${actualId}`, {
          headers: { 'X-WP-Nonce': config.apiSettings.nonce }
        });
        templateData = response.data;
      }
      
      // Ensure workflow_data is properly parsed
      let workflowData = templateData.workflow_data;
      if (typeof workflowData === 'string') {
        workflowData = JSON.parse(workflowData);
      }
      
      const updatedNodes = workflowData.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          updateNodeData,
          onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id))
        }
      }));
  
      setNodes(updatedNodes);
      setEdges(workflowData.edges);
      setIsTemplateSelectionModalVisible(false);
      setIsEditingTemplate(true);
      if (templateData.name) {
        setWorkflowName(templateData.name);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      message.error('Failed to load template');
    }
  };

  const handleCreateNew = () => {
    setIsTemplateSelectionModalVisible(false);
    setIsEditingTemplate(false);
    // Set up initial empty workflow with a timestamp-based ID
    const randomId = `trigger-${Date.now()}`;
    setNodes([
      { 
        id: randomId, 
        type: 'trigger', 
        position: { x: 250, y: 25 }, 
        data: { 
          label: 'Trigger Node', 
          updateNodeData,
          onDelete: (id) => {
            setNodes((nds) => nds.filter((node) => node.id !== id));
          },
          triggerType: '',
          content: '',
          selectedForm: null,
          selectedFields: []
        }
      }
    ]);
    setEdges([]);
  };

  const hasCycle = (graph, start, visited = new Set(), recursionStack = new Set()) => {
    visited.add(start);
    recursionStack.add(start);
  
    // Get all nodes this node connects to
    const neighbors = graph[start] || [];
    
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (hasCycle(graph, neighbor, visited, recursionStack)) {
          return true;
        }
      } else if (recursionStack.has(neighbor)) {
        return true;
      }
    }
  
    recursionStack.delete(start);
    return false;
  };
  
  // Create adjacency list from edges
  const createGraph = (edges) => {
    const graph = {};
    edges.forEach(edge => {
      if (!graph[edge.source]) {
        graph[edge.source] = [];
      }
      graph[edge.source].push(edge.target);
    });
    return graph;
  };
  
  // Check if adding a new edge would create a cycle
  const wouldCreateCycle = (edges, newEdge) => {
    // Add the new edge to existing edges
    const allEdges = [...edges, newEdge];
    const graph = createGraph(allEdges);
    
    // Check for cycles starting from the source of new edge
    return hasCycle(graph, newEdge.source);
  };

  const onConnect = useCallback((params) => {
    // First check if this connection would create a cycle
    if (wouldCreateCycle(edges, params)) {
      message.error('Cannot create connection: This would create a loop in the workflow');
      return;
    }
    
    // If no cycle would be created, add the edge
    setEdges((eds) => addEdge(params, eds));
  }, [edges, setEdges]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleSaveAsTemplate = () => {
    setIsSaveTemplateModalVisible(true);
  };

  const handleTemplateSave = async () => {
    if (!templateName.trim()) {
      message.error('Please enter a template name');
      return;
    }
  
    try {
      const flow = reactFlowInstance.toObject();
      const templateData = {
        name: templateName,
        description: templateDescription,
        workflow_data: JSON.stringify({ nodes: flow.nodes, edges: flow.edges })
      };
      await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/templates`, templateData, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      message.success('Template saved successfully');
      setIsSaveTemplateModalVisible(false);
      setTemplateName('');
      setTemplateDescription('');
    } catch (error) {
      console.error('Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
  
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
  
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
  
      let newNode;
  
      // Handle annotation nodes
      switch (type) {
        case 'stickyNote':
          newNode = {
            id: `note-${Date.now()}`,
            type,
            position,
            data: { 
              content: '',
              color: '#fff9c4',
              size: { width: 200, height: 150 },
              onChange: (id, updates) => {
                setNodes(nds => 
                  nds.map(node => 
                    node.id === id 
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            ...updates,
                            onChange: node.data.onChange,
                            onDelete: node.data.onDelete 
                          } 
                        }
                      : node
                  )
                );
              },
              onDelete: (id) => {
                setNodes(nds => nds.filter(node => node.id !== id));
              }
            },
            draggable: true,
            className: 'react-flow-annotation',
            zIndex: -1
          };
          break;
  
        case 'textAnnotation':
          newNode = {
            id: `text-${Date.now()}`,
            type,
            position,
            data: {
              content: '',
              fontSize: 14,
              size: { width: 200, height: 'auto' },
              onChange: (id, updates) => {
                setNodes(nds => 
                  nds.map(node => 
                    node.id === id 
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            ...updates,
                            onChange: node.data.onChange,
                            onDelete: node.data.onDelete 
                          } 
                        }
                      : node
                  )
                );
              },
              onDelete: (id) => {
                setNodes(nds => nds.filter(node => node.id !== id));
              }
            },
            draggable: true,
            className: 'react-flow-annotation',
            zIndex: -1
          };
          break;
  
        case 'shape':
          newNode = {
            id: `shape-${Date.now()}`,
            type,
            position,
            data: {
              shapeType: 'rectangle',
              color: '#e8e8e8',
              size: { width: 100, height: 100 },
              onChange: (id, updates) => {
                setNodes(nds => 
                  nds.map(node => 
                    node.id === id 
                      ? { 
                          ...node, 
                          data: { 
                            ...node.data, 
                            ...updates,
                            onChange: node.data.onChange,
                            onDelete: node.data.onDelete 
                          } 
                        }
                      : node
                  )
                );
              },
              onDelete: (id) => {
                setNodes(nds => nds.filter(node => node.id !== id));
              }
            },
            draggable: true,
            className: 'react-flow-annotation',
            zIndex: -1
          };
          break;
  
        default:
          // Handle regular workflow nodes
          newNode = {
            id: `${type}-${Date.now()}`,
            type,
            position,
            draggable: true,
            data: { 
              updateNodeData,
              onDelete: (id) => setNodes((nds) => nds.filter((n) => n.id !== id)),
            },
          };
      }
  
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, updateNodeData]
  );

  const onSave = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const onUpdate = useCallback(async () => {
    if (!workflowId) {
      message.error('Cannot update: No workflow ID found');
      return;
    }
    try {
      const flow = reactFlowInstance.toObject();
      const updatedWorkflow = {
        id: workflowId,
        name: workflowName,
        nodes: flow.nodes,
        edges: flow.edges,
        viewport: flow.viewport
      };
      const response = await axios.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`, updatedWorkflow, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      if (response.data) {
        message.success('Workflow updated successfully');
      }
    } catch (error) {
      console.error('Error updating workflow:', error);
      message.error('Failed to update workflow');
    }
  }, [workflowId, workflowName, reactFlowInstance]);

  const handleSaveWorkflow = async () => {
    if (!workflowName.trim()) {
      message.error('Please enter a workflow name');
      return;
    }
    try {
      const method = workflowId ? 'put' : 'post';
      const url = workflowId
        ? `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`
        : `${config.apiSettings.root}wp-ai-workflows/v1/workflows`;
      
      const flow = reactFlowInstance.toObject();
      
      // Preserve annotation data by explicitly including it
      const annotationNodes = flow.nodes.map(node => {
        if (['stickyNote', 'textAnnotation', 'shape'].includes(node.type)) {
          return {
            ...node,
            data: {
              ...node.data,
              // Preserve only the necessary data properties
              content: node.data.content,
              color: node.data.color,
              size: node.data.size,
              fontSize: node.data.fontSize,
              shapeType: node.data.shapeType
            }
          };
        }
        return node;
      });
  
      const workflowData = {
        id: workflowId,
        name: workflowName,
        nodes: annotationNodes,
        edges: flow.edges,
        viewport: flow.viewport
      };
  
      const response = await axios[method](url, workflowData, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
  
      if (response.data && response.data.id) {
        const newWorkflowId = response.data.id;
        setWorkflowId(newWorkflowId);
        setIsSavedWorkflow(true);
  
        // Update nodes with the new workflowId and preserve annotation data
        const updatedNodes = annotationNodes.map(node => {
          if (node.type === 'output' || node.type === 'chat') {
            return {
              ...node,
              data: {
                ...node.data,
                workflowId: newWorkflowId
              }
            };
          }
          return node;
        });
  
        reactFlowInstance.setNodes(updatedNodes);
      }
  
      message.success('Workflow saved successfully');
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving workflow:', error);
      message.error('Failed to save workflow: ' + (error.response?.data?.message || error.message));
    }
  };

  const executeWorkflow = async () => {
    if (!workflowId) {
        message.error('Please save the workflow before executing');
        return;
    }

    setIsExecuting(true);
    setExecutionError(false);
    setCompletionTime(null);
    setExecutionProgress([]);
    
    let pollInterval;
    let timeoutCounter = 0;
    const MAX_TIMEOUT = 600; 

    try {
        const response = await adminInstance.post(
            `${config.apiSettings.root}wp-ai-workflows/v1/execute-workflow/${workflowId}`,
            {
                nodes: nodes,
                edges: edges,
            }
        );

        setExecutionId(response.data.execution_id);

        console.log('Raw response:', response);

        const executionId = response.data.execution_id;

        if (!executionId) {
            throw new Error('Invalid response from server: No execution ID received');
        }

        // Start polling
        pollInterval = setInterval(async () => {
            try {
                timeoutCounter++;
                if (timeoutCounter >= MAX_TIMEOUT) {
                    clearInterval(pollInterval);
                    setExecutionError(true);
                    setIsExecuting(false);
                    setCompletionTime(new Date().toLocaleTimeString());
                    message.error('Execution timed out after 4 minutes');
                    return;
                }

                const statusResponse = await adminInstance.get(
                    `${config.apiSettings.root}wp-ai-workflows/v1/execution-status/${executionId}`
                );

                const { status, current_node, nodes: executedNodes, is_complete} = statusResponse.data;

                if (current_node) {
                    const currentNodeData = nodes.find(n => n.id === current_node);
                    if (currentNodeData) {
                        setCurrentExecutingNode(currentNodeData.data.nodeName || current_node);
                    }
                }

                // Process completed nodes
                if (executedNodes) {
                    const updatedNodes = [...nodes];
                    
                    Object.entries(executedNodes).forEach(([nodeId, nodeData]) => {
                        const nodeIndex = updatedNodes.findIndex(n => n.id === nodeId);
                        if (nodeIndex !== -1) {
                            updatedNodes[nodeIndex] = {
                                ...updatedNodes[nodeIndex],
                                data: {
                                    ...updatedNodes[nodeIndex].data,
                                    displayOutput: nodeData.content,
                                    output: nodeData.content,
                                    isExecuting: false,
                                    executed: true
                                }
                            };
                            
                            const nodeName = updatedNodes[nodeIndex].data.nodeName || nodeId;
                            setExecutionProgress(prev => {
                                if (!prev.includes(nodeName)) {
                                    return [...prev, nodeName];
                                }
                                return prev;
                            });
                        }
                    });

                    setNodes(updatedNodes);
                }

                if (is_complete || status === 'completed' || status === 'error') {
                    clearInterval(pollInterval);
                    setIsExecuting(false);
                    setCurrentExecutingNode(null);
                    setCompletionTime(new Date().toLocaleTimeString());
                    if (status === 'error') {
                        setExecutionError(true);
                        message.error('Workflow execution failed');
                    } else {
                        message.success('Workflow executed successfully');
                    }
                }
            } catch (error) {
                console.error('Error polling execution status:', error);
                clearInterval(pollInterval);
                setExecutionError(true);
                setIsExecuting(false);
                setCompletionTime(new Date().toLocaleTimeString());
                message.error('Error during workflow execution');
            }
        }, 500);

    } catch (error) {
        console.error('Error executing workflow:', error);
        setExecutionError(true);
        setIsExecuting(false);
        setCompletionTime(new Date().toLocaleTimeString());
        message.error('Failed to execute workflow: ' + (error.response?.data?.message || error.message));
    }

    // Return cleanup function
    return () => {
        if (pollInterval) {
            clearInterval(pollInterval);
        }
    };
};

  return (
    <div
    className="reactflow-wrapper" 
    style={{ 
      height: '90vh',
      position: 'relative',
       overflow: 'hidden'
    }}>
      <div style={{ 
        display: 'flex',
        height: '100%',
      }}>
        {/* Sidebar */}
        <div style={{ 
          width: '200px', 
          borderRight: '1px solid #ddd', 
          height: '100%', 
          zIndex: 5,
          flexShrink: 0
        }}>
          <Sidebar
            updateNodeData={updateNodeData}
            onSave={onSave}
            onExecute={executeWorkflow}
            onUpdate={onUpdate}
            onSaveAsTemplate={handleSaveAsTemplate}
            isEditMode={isEditMode}
            isExecuting={isExecuting}
            isEditingTemplate={isEditingTemplate}
            isSavedWorkflow={isSavedWorkflow}
            nodes={nodes}
            edges={edges}
            workflowName={workflowName}
            onActionModalOpen={handleActionModalOpen} 
            darkMode={colorMode === 'dark'}
          />
        </div>
  
        {/* Right side container */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Name header */}
          <WorkflowNameHeader
          initialName={workflowName}
          isEditMode={isEditMode}
          onNameChange={(newName) => {
            setWorkflowName(newName);
            if (isEditMode || isSavedWorkflow) {
              const flow = reactFlowInstance.toObject();
              const updatedWorkflow = {
                id: workflowId,
                name: newName,
                nodes: flow.nodes,
                edges: flow.edges,
                viewport: flow.viewport
              };
              
              adminInstance.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`, 
                updatedWorkflow,
                {
                  headers: {
                    'X-WP-Nonce': config.apiSettings.nonce
                  }
                }
              ).then(() => {
                message.success('Workflow name updated successfully');
              }).catch((error) => {
                console.error('Error updating workflow name:', error);
                message.error('Failed to update workflow name');
              });
            }
          }}
        />
          
          {/* Execution Status Bar */}
          <ExecutionStatusBar 
            isExecuting={isExecuting}
            currentNode={currentExecutingNode}
            completionTime={completionTime}
            hasError={executionError}
            progress={executionProgress}
            onViewDetails={() => {
              if (executionId) { // Make sure we have an executionId
                viewExecutionDetails(executionId);
              }
            }}
          />

          <WorkflowDetailsModal
            execution={selectedExecution}
            visible={isDetailsModalVisible}
            onClose={() => {
              setIsDetailsModalVisible(false);
              setSelectedExecution(null);
            }}
          />
  
          {/* Flow container */}
          <div style={{ 
            height: '100%',
            width: '100%'
          }} ref={reactFlowWrapper}>

          <ReactFlow
            nodes={nodes}
            edges={edges.map(edge => ({
              ...edge,
              data: { 
                onDelete: handleEdgeDelete,
                colorMode: colorMode
              }
            }))}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            colorMode={colorMode}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ 
              type: 'default',
              animated: false
            }}
            onlyRenderVisibleElements={true}
            nodesFocusable={false}
            edgesFocusable={false}
            elevateNodesOnSelect={false}
            minZoom={0.1} 
            maxZoom={4} 
          >
            <Controls />
            <MiniMap pannable />
            <Background variant="dots" gap={12} size={1} />
            {/*
            <WorkflowAssistant
            ref={workflowAssistantRef}
            workflow={{
                nodes,
                edges,
                viewport: reactFlowInstance?.getViewport(),
                name: workflowName,
                id: workflowId,
                isExecuting,
                isSaved: isSavedWorkflow,
                onWorkflowUpdate: (updatedWorkflow) => {
                  console.log('Applying workflow updates to UI...');
                  
                  // Handle node changes
                  updatedWorkflow.nodes.forEach(updatedNode => {
                    const currentNode = nodes.find(n => n.id === updatedNode.id);
                    if (!currentNode?.data) return;
                
                    // Common properties handler
                    const commonProperties = ['nodeName', 'content', 'workflowId'];
                    commonProperties.forEach(prop => {
                      if (updatedNode.data[prop] !== undefined && currentNode.data[`handle${prop.charAt(0).toUpperCase() + prop.slice(1)}Change`]) {
                        currentNode.data[`handle${prop.charAt(0).toUpperCase() + prop.slice(1)}Change`](
                          currentNode.id,
                          { [prop]: updatedNode.data[prop] }
                        );
                      }
                    });
                
                    // Settings handler (for nodes with settings)
                    if (updatedNode.data.settings) {
                      Object.entries(updatedNode.data.settings).forEach(([setting, value]) => {
                        currentNode.data.handleSettingChange?.(setting, value);
                      });
                    }
                
                    // Model params handler (for Chat node)
                    if (updatedNode.data.modelParams) {
                      Object.entries(updatedNode.data.modelParams).forEach(([param, value]) => {
                        currentNode.data.handleModelParamChange?.(param, value);
                      });
                    }
                
                    // Specialized properties handlers
                    const specializedProps = {
                      extractionFields: 'handleExtractionFieldsChange',
                      fieldMappings: 'handleFieldMappingsChange',
                      conditionGroups: 'handleConditionGroupsChange',
                      auth: 'handleAuthChange',
                      design: 'handleDesignChange',
                      behavior: 'handleBehaviorChange'
                    };
                
                    Object.entries(specializedProps).forEach(([prop, handler]) => {
                      if (updatedNode.data[prop] !== undefined && currentNode.data[handler]) {
                        currentNode.data[handler](updatedNode.data[prop]);
                      }
                    });
                  });
                
                  // Handle edge changes
                  if (updatedWorkflow.edges) {
                    const edgeChanges = {
                      added: updatedWorkflow.edges.filter(edge => !edges.find(e => e.id === edge.id)),
                      removed: edges.filter(edge => !updatedWorkflow.edges.find(e => e.id === edge.id)),
                      modified: updatedWorkflow.edges.filter(edge => {
                        const currentEdge = edges.find(e => e.id === edge.id);
                        return currentEdge && (
                          edge.source !== currentEdge.source ||
                          edge.target !== currentEdge.target ||
                          edge.sourceHandle !== currentEdge.sourceHandle ||
                          edge.targetHandle !== currentEdge.targetHandle
                        );
                      })
                    };
                
                    if (edgeChanges.added.length || edgeChanges.removed.length || edgeChanges.modified.length) {
                      setEdges(updatedWorkflow.edges);
                    }
                  }
                
                  message.success('Workflow changes applied successfully');
                }
            }}
            selectedNode={getSelectedNode()}
            colorMode={colorMode}
            onNodeSelect={(node) => {
                reactFlowInstance?.setNodes((nds) =>
                    nds.map((n) => ({
                        ...n,
                        selected: n.id === (node?.id || null)
                    }))
                );
            }}
        /> */}
              <AnnotationToolbar />
            
          </ReactFlow>
          </div>
        </div>
      </div>
      <TemplateModal
        visible={isTemplateSelectionModalVisible}
        onCancel={() => setIsTemplateSelectionModalVisible(false)}
        onSelectTemplate={handleSelectTemplate}
        onImport={handleImport}
        onCreateNew={handleCreateNew}
      />

      <Modal
        title="Save as Template"
        visible={isSaveTemplateModalVisible}
        onOk={handleTemplateSave}
        onCancel={() => setIsSaveTemplateModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Template Name" required>
            <Input 
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
            />
          </Form.Item>
          <Form.Item label="Template Description">
            <Input.TextArea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Enter template description (optional)"
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        className="workflow-name-input"
        title="Save Workflow"
        visible={isModalVisible}
        onOk={handleSaveWorkflow}
        onCancel={() => setIsModalVisible(false)}
      >
        <Input
          placeholder="Enter workflow name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
        />
      </Modal>
      <WorkflowActionsModal
          visible={isActionsModalVisible}
          onCancel={() => setIsActionsModalVisible(false)}
          onExport={handleExport} 
          onImport={handleImport}
          workflowName={workflowName}
          workflowId={workflowId}
          onOpenEmbedModal={() => {
            setIsActionsModalVisible(false);
            setEmbedModalVisible(true);
          }}
        />

      {workflowId && (
      <EmbedCodeModal
        visible={embedModalVisible}
        workflowId={workflowId}
        onClose={() => setEmbedModalVisible(false)}
      />
    )}

      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, display: 'flex', gap: '12px', alignItems: 'center' }}>
      <Switch
        checked={colorMode === 'dark'}
        onChange={toggleDarkMode}
        checkedChildren={<MoonOutlined />}
        unCheckedChildren={<SunOutlined />}
      />
      
      <Button
        type="primary"
        icon={<BulbOutlined />}
        onClick={() => window.open('https://wpaiworkflowautomation.com/docs/', '_blank')}
      >
        Documentation
      </Button>
      </div>
    </div>
  );
};

const WorkflowBuilder = () => (
  <ReactFlowProvider>
    <WorkflowBuilderInner />
  </ReactFlowProvider>
);

export default WorkflowBuilder;